import React, { useState } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import DialogContent from '@material-ui/core/DialogContent';
import { ITransformer } from '../../../../@types/model/transformer/transformer';
import StandardFab from '../../../customComponents/button/StandardFab';
import AddIcon from '@material-ui/icons/Add';
import TransformerDropdown from '../../../customComponents/selector/transformer/TransformerSelector';
import BankDropdown from '../../../customComponents/selector/BankSelector';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import { BankAccountType } from '../../../../@types/model/bankAccount';
import BankAccountTypeDropdown from '../../../customComponents/selector/BankAccountTypeSelector';
import OutlinedStadiumButton from '../../../customComponents/button/OutlinedStadiumButton';
import StadiumButton from '../../../customComponents/button/StadiumButton';
import TransformerFunctions from '../../../../store/transformer/functions';
import ProjectDialog from '../../../customComponentV2/dialog/ProjectDialog';

interface ITransformerAccountEditDialogProps {
    transformer ?: ITransformer;
    excludeTransformers ?: Array<ITransformer>;
    isLoading : boolean;
}

const TransformerAccountEditDialog = (props: ITransformerAccountEditDialogProps): React.ReactElement => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [epNumber, setEpNumber] = useState<string | undefined>(undefined);
    const [bank, setBank] = useState<string | undefined>(undefined);
    const [accountNumber, setAccountNumber] = useState<string>('');
    const [accountType, setAccountType] = useState<BankAccountType | undefined>(undefined);
    const [branch, setBranch] = useState<string>('');
    const [beneficiaryDescription, setBeneficiaryDescription] = useState<string>('');
    const [recipientReference, setRecipientReference] = useState<string>('');
    const [ownReference, setOwnReference] = useState<string>('');

    const onClick = () : void => {
        setIsOpen(true);

        if (props.transformer?.BankAccount) {
            setAccountNumber(props.transformer.BankAccount.accountNumber);
            setBranch(props.transformer.BankAccount.branch);
            setBeneficiaryDescription(props.transformer.BankAccount.beneficiaryDescription);
            setRecipientReference(props.transformer.BankAccount.recipientReference);
            setOwnReference(props.transformer.BankAccount.ownReference);
            setAccountType(props.transformer.BankAccount.accountType);
            setBank(props.transformer.BankAccount.bank);
            setEpNumber(props.transformer.EPNumber);
        } else {
            setAccountNumber('');
            setBranch('');
            setBeneficiaryDescription('');
            setRecipientReference('');
            setOwnReference('');
            setAccountType(undefined);
            setBank(undefined);
            setEpNumber(undefined);
        }
    };

    const onClose = () : void => {
        setIsOpen(false);
    };

    const save = async (
        currentEpNumber: string,
        currentBank: string,
        currentAccountNumber: string,
        currentAccountType: BankAccountType,
        currentBranch: string,
        currentBeneficiaryDescription: string,
        currentRecipientReference: string,
        currentOwnReference: string
    ) : Promise<void> => {
        const result = await TransformerFunctions.updateBankDetails(
            currentEpNumber,
            currentBank,
            currentAccountNumber,
            currentAccountType,
            currentBranch,
            currentBeneficiaryDescription,
            currentRecipientReference,
            currentOwnReference
        );

        if (result) {
            onClose();
        }
    };

    const onEPNumberChange = (value?: ITransformer) : void => {
        setEpNumber(value?.EPNumber);
    };

    const onBankChange = (value?: string) : void => {
        setBank(value);
    };

    const onAccountNumberChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : void => {
        setAccountNumber(event.currentTarget.value);
    };

    const onAccountTypeChange = (value?: BankAccountType) : void => {
        setAccountType(value);
    };

    const onBranchCodeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : void => {
        setBranch(event.currentTarget.value);
    };

    const onBeneficiaryDescriptionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : void => {
        setBeneficiaryDescription(event.currentTarget.value);
    };

    const onRecipientReferenceChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : void => {
        setRecipientReference(event.currentTarget.value);
    };

    const onOwnReferenceChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : void => {
        setOwnReference(event.currentTarget.value);
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) : void => {
        event.preventDefault();

        if (!epNumber || !bank || !accountNumber || !accountType || !branch) return;
        if (!beneficiaryDescription || !ownReference || !recipientReference) return;

        save(
            epNumber,
            bank,
            accountNumber,
            accountType,
            branch,
            beneficiaryDescription,
            recipientReference,
            ownReference
        );
    };
        
    return (
        <div>
            <Tooltip title={!props.transformer ? 'Add Account' : 'Edit Account'}>
                <div>
                    {
                        !props.transformer &&
                        <StandardFab aria-label='add' disabled={props.isLoading} onClick={onClick}>
                            <AddIcon />
                        </StandardFab>
                    }
                    {
                        props.transformer &&
                        <IconButton color='primary' onClick={onClick}
                            aria-label='Edit' disabled={props.isLoading}>
                            <Icon>edit</Icon>
                        </IconButton>
                    }
                </div>
            </Tooltip>
            <ProjectDialog
                isOpen={isOpen}
                title={!props.transformer ? 'Create Account' : 'Edit Account'}
                onClose={onClose}
                maxWidth={'md'}
                isLoadingCircular={props.isLoading}
            >
                <form autoComplete='off' onSubmit={onSubmit} className='fdc flx1 hfill'>
                    <DialogContent className='fdc flx1 ais hfill'>
                        <div className='fdc flx1 ais jcc'>
                            <div className='fdr ais'>
                                <div className='fdc flx1 pr5'>
                                    <TransformerDropdown
                                        value={epNumber}
                                        label='Waypoint No'
                                        fullWidth
                                        exclusionList={props.excludeTransformers}
                                        onChange={onEPNumberChange}
                                        required
                                        disabled={!!props.transformer} // Disable if editing, EPNumber shouldn't change for editing account
                                    />
                                </div>
                                <div className='fdc flx1 pl5'>
                                    <BankDropdown
                                        value={bank}
                                        fullWidth
                                        onChange={onBankChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className='fdr ais'>
                                <div className='fdc flx1 aifs pr5'>
                                    <FormControl fullWidth required>
                                        <TextField
                                            autoComplete='off'
                                            id='accountNumber'
                                            label='Account Number'
                                            value={accountNumber}
                                            onChange={onAccountNumberChange}
                                            margin='normal'
                                            required
                                            error={!accountNumber}
                                            className={'TextField'}
                                        />
                                        {
                                            !accountNumber &&
                                            <FormHelperText error>Required</FormHelperText>
                                        }
                                    </FormControl>
                                </div>
                                <div className='fdc flx1 aifs pl5 mt16'>
                                    <BankAccountTypeDropdown
                                        value={accountType}
                                        fullWidth
                                        onChange={onAccountTypeChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className='fdr ais'>
                                <div className='fdc flx1 aifs pr5'>
                                    <FormControl fullWidth required>
                                        <TextField
                                            autoComplete='off'
                                            id='branchCode'
                                            label='Branch Code'
                                            value={branch}
                                            onChange={onBranchCodeChange}
                                            margin='normal'
                                            required
                                            error={!branch}
                                            className={'TextField'}
                                        />
                                        {
                                            !branch &&
                                            <FormHelperText error>Required</FormHelperText>
                                        }
                                    </FormControl>
                                </div>
                                <div className='fdc flx1 aifs pl5'>
                                    <FormControl fullWidth required>
                                        <TextField
                                            autoComplete='off'
                                            id='beneficiaryDescription'
                                            label='Beneficiary Description'
                                            value={beneficiaryDescription}
                                            onChange={onBeneficiaryDescriptionChange}
                                            margin='normal'
                                            required
                                            error={!beneficiaryDescription}
                                            className={'TextField'}
                                        />
                                        {
                                            !beneficiaryDescription &&
                                            <FormHelperText error>Required</FormHelperText>
                                        }
                                    </FormControl>
                                </div>
                            </div>
                            <div className='fdr ais'>
                                <div className='fdc flx1 aifs pr5'>
                                    <FormControl fullWidth required>
                                        <TextField
                                            autoComplete='off'
                                            id='recipientReference'
                                            label='Recipient Reference'
                                            value={recipientReference}
                                            onChange={onRecipientReferenceChange}
                                            margin='normal'
                                            required
                                            error={!recipientReference}
                                            className={'TextField'}
                                        />
                                        {
                                            !recipientReference &&
                                            <FormHelperText error>Required</FormHelperText>
                                        }
                                    </FormControl>
                                </div>
                                <div className='fdc flx1 aifs pl5'>
                                    <FormControl fullWidth required>
                                        <TextField
                                            autoComplete='off'
                                            id='ownReference'
                                            label='Own Reference'
                                            value={ownReference}
                                            onChange={onOwnReferenceChange}
                                            margin='normal'
                                            required
                                            error={!ownReference}
                                            className={'TextField'}
                                        />
                                        {
                                            !ownReference &&
                                            <FormHelperText error>Required</FormHelperText>
                                        }
                                    </FormControl>
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                    <div style={{ padding: '8px 24px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                        <OutlinedStadiumButton disabled={props.isLoading} style={{ marginRight: '8px' }} variant='text' type='button' onClick={onClose}>
                            CANCEL
                        </OutlinedStadiumButton>
                        <StadiumButton disabled={props.isLoading} type='submit'>
                            SAVE
                        </StadiumButton>
                    </div>
                </form>
            </ProjectDialog>
        </div>
    );
};

export default TransformerAccountEditDialog;
